import { useState } from "react";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useSeriesSuggestions } from "@/hooks/useSeriesSuggestions";
import { useSeriesStats } from "@/hooks/useSeriesStats";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, X, Edit, BookOpen, TrendingUp, AlertCircle, Search, Plus, Sparkles, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { SearchSeriesBookDialog } from "@/components/admin/SearchSeriesBookDialog";
import { EditSeriesDialog } from "@/components/admin/EditSeriesDialog";
import { AISuggestSeriesDialog } from "@/components/admin/AISuggestSeriesDialog";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useQueryClient, useQuery } from "@tanstack/react-query";

export default function AdminSeriesManagement() {
  const { isAdmin, loading } = useIsAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState("pending");
  const [searchBookOpen, setSearchBookOpen] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<any>(null);
  const [aiSuggestOpen, setAiSuggestOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingSeries, setEditingSeries] = useState<any>(null);
  const [editSeriesDialogOpen, setEditSeriesDialogOpen] = useState(false);
  
  const { 
    suggestions, 
    allSuggestions,
    isLoading: suggestionsLoading,
    approveSuggestion,
    rejectSuggestion,
    updateSuggestion,
    isApproving,
  } = useSeriesSuggestions();

  const { data: stats, isLoading: statsLoading } = useSeriesStats();

  // Fetch series from imported books data
  const { data: importedSeries } = useQuery({
    queryKey: ['imported-series'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('books')
        .select('series_name, author, authors, id, title, cover_url, series_position, series_total')
        .not('series_name', 'is', null)
        .order('series_name', { ascending: true })
        .order('series_position', { ascending: true });
      
      if (error) throw error;

      // Group books by series
      const seriesMap = new Map<string, any>();
      
      data?.forEach(book => {
        const key = `${book.series_name}-${(book.authors?.[0] || book.author || 'Unknown')}`;
        if (!seriesMap.has(key)) {
          seriesMap.set(key, {
            seriesName: book.series_name,
            authorName: book.authors?.[0] || book.author || 'Unknown',
            books: [],
            expectedBooks: book.series_total || 0,
          });
        }
        seriesMap.get(key).books.push({
          id: book.id,
          title: book.title,
          position: book.series_position || 0,
          coverUrl: book.cover_url,
        });
      });

      return Array.from(seriesMap.values());
    },
  });

  const handleAddBook = (suggestionId: string, seriesName: string, authorName: string, position?: number, tempTitle?: string, tempIsbn?: string) => {
    setSelectedSeries({ suggestionId, seriesName, authorName, position, tempTitle, tempIsbn });
    setSearchBookOpen(true);
  };

  const handleBookSelected = async (bookId: string, bookData: any) => {
    console.log('Book selected:', bookId, bookData);
    
    if (!selectedSeries?.suggestionId || !selectedSeries?.position) {
      toast({
        title: "Error",
        description: "Missing series information",
        variant: "destructive",
      });
      return;
    }

    try {
      // Find the series_books record for this position and update it
      const { data: seriesBook, error: fetchError } = await supabase
        .from('series_books')
        .select('id')
        .eq('series_suggestion_id', selectedSeries.suggestionId)
        .eq('position_in_series', selectedSeries.position)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (seriesBook) {
        // Update existing record
        const { error: updateError } = await supabase
          .from('series_books')
          .update({
            book_id: bookId,
            is_found: true,
            temp_title: null,
            temp_isbn13: null,
          })
          .eq('id', seriesBook.id);

        if (updateError) throw updateError;
      } else {
        // Create new record if it doesn't exist
        const { error: insertError } = await supabase
          .from('series_books')
          .insert({
            series_suggestion_id: selectedSeries.suggestionId,
            book_id: bookId,
            position_in_series: selectedSeries.position,
            is_found: true,
          });

        if (insertError) throw insertError;
      }

      toast({
        title: "Success",
        description: "Book linked to series",
      });

      // Invalidate and refetch the series suggestions to show the updated data
      queryClient.invalidateQueries({ queryKey: ['series-suggestions'] });

      // Close dialog
      setSearchBookOpen(false);
    } catch (error: any) {
      console.error('Error linking book:', error);
      toast({
        title: "Error",
        description: `Failed to link book: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleEditSeries = async (suggestion: any) => {
    // Fetch the full series data with properly joined books
    const { data: seriesData, error } = await supabase
      .from('series_suggestions')
      .select(`
        id,
        series_name,
        author_name,
        series_books (
          id,
          position_in_series,
          book_id,
          temp_title,
          temp_isbn13,
          book:books (
            title,
            authors,
            cover_url
          )
        )
      `)
      .eq('id', suggestion.id)
      .single();

    if (error) {
      console.error('Error loading series:', error);
      toast({
        title: "Error",
        description: "Failed to load series data",
        variant: "destructive",
      });
      return;
    }

    setEditingSeries(seriesData);
    setEditSeriesDialogOpen(true);
  };

  const handleAISeriesSelected = async (seriesData: any) => {
    setIsCreating(true);
    try {
      // Normalize names for searching
      const normalizedSeriesName = seriesData.seriesName.toLowerCase().trim();
      const normalizedAuthorName = seriesData.authorName.toLowerCase().trim();

      // Create a new series suggestion in the database
      const { data: newSuggestion, error } = await supabase
        .from('series_suggestions')
        .insert({
          series_name: seriesData.seriesName,
          series_name_normalized: normalizedSeriesName,
          author_name: seriesData.authorName,
          author_name_normalized: normalizedAuthorName,
          source: 'ai',
          confidence_score: 9.0, // High confidence (scale 0-10)
          status: 'pending',
          total_books_expected: seriesData.books?.length || 0
        })
        .select()
        .maybeSingle();

      if (error) throw error;

      // Create series_books records for the AI-suggested books
      if (newSuggestion && seriesData.books?.length > 0) {
        const bookPromises = seriesData.books.map(async (book: any) => {
          // First, try to find if this book exists in our database by ISBN
          let bookId = null;
          
          if (book.isbn13) {
            const { data: existingBook } = await supabase
              .from('books')
              .select('id')
              .eq('isbn13', book.isbn13)
              .maybeSingle();
            
            bookId = existingBook?.id;
          }
          
          // Create the series_books record with temp data
          await supabase
            .from('series_books')
            .insert({
              series_suggestion_id: newSuggestion.id,
              book_id: bookId,
              position_in_series: book.position,
              is_found: !!bookId,
              temp_title: book.title,
              temp_isbn13: book.isbn13 || null,
            });
        });

        await Promise.all(bookPromises);
      }

      toast({
        title: "Series suggestion created",
        description: `${seriesData.seriesName} with ${seriesData.books?.length || 0} books has been added to pending suggestions.`,
      });

      // Refresh the suggestions list
      queryClient.invalidateQueries({ queryKey: ['series-suggestions'] });

      setAiSuggestOpen(false);
    } catch (error) {
      console.error('Error creating series suggestion:', error);
      toast({
        title: "Error",
        description: "Failed to create series suggestion. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleBookClick = (bookId: string) => {
    navigate(`/book/${bookId}`);
  };

  if (loading || suggestionsLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-destructive">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-[1600px]">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Series Management</h1>
            <p className="text-muted-foreground">Review and manage book series suggestions</p>
          </div>
          <Button onClick={() => setAiSuggestOpen(true)} size="lg">
            <Sparkles className="mr-2 h-4 w-4" />
            AI Suggest Series
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Review</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.pendingSuggestions ?? 0}</div>
            {(stats?.pendingSuggestions ?? 0) > 0 && (
              <Badge variant="secondary" className="mt-2">
                <AlertCircle className="mr-1 h-3 w-3" />
                Needs attention
              </Badge>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Verified Series</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.verifiedSeries ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-2">Across {statsLoading ? '...' : stats?.totalBooksWithSeries ?? 0} books</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Coverage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.coveragePercent ?? 0}%</div>
            <Progress value={stats?.coveragePercent ?? 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Series with Gaps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.seriesWithGaps ?? 0}</div>
            <Badge variant="outline" className="mt-2">
              Missing books
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Health Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {statsLoading ? '...' : Math.min(10, Math.round((stats?.coveragePercent ?? 0) / 10))}/ 10
            </div>
            <div className="flex items-center mt-2 text-xs text-green-600">
              <TrendingUp className="mr-1 h-3 w-3" />
              Based on coverage
            </div>
          </CardContent>
        </Card>
      </div>

        {/* Search and Filters */}
        <div className="mb-6">
          <div className="relative max-w-2xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search series, author, or ISBN..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="pending" className="gap-2">
              Pending Suggestions
              <Badge variant="secondary">{suggestions.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="verified">Verified Series</TabsTrigger>
            <TabsTrigger value="gaps">Missing Books</TabsTrigger>
            <TabsTrigger value="all">All Series</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-0">
          <div className="space-y-4">
            {suggestions.map((suggestion) => (
              <Card key={suggestion.id} className="overflow-hidden">
                <CardHeader className="bg-muted/30">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <CardTitle className="text-xl">{suggestion.seriesName}</CardTitle>
                        <Badge
                          variant={suggestion.confidence >= 90 ? "default" : suggestion.confidence >= 70 ? "secondary" : "outline"}
                        >
                          {suggestion.confidence}% confidence
                        </Badge>
                        <Badge variant="outline">
                          {suggestion.source === "isbndb" ? "ISBNdb" : suggestion.source === "pattern_match" ? "Pattern Match" : "AI Detection"}
                        </Badge>
                      </div>
                      <CardDescription className="text-base">
                        by {suggestion.authorName} • {suggestion.booksFound} of {suggestion.expectedBooks} books found
                        {suggestion.gaps.length > 0 && (
                          <span className="text-orange-600 ml-2">
                            • Missing: Book {suggestion.gaps.join(", Book ")}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEditSeries(suggestion)}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Edit Series
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-destructive hover:text-destructive"
                        onClick={() => rejectSuggestion(suggestion.id)}
                      >
                        <X className="mr-2 h-4 w-4" />
                        Reject
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => approveSuggestion(suggestion.id)}
                      >
                        <Check className="mr-2 h-4 w-4" />
                        Approve
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ScrollArea className="w-full">
                    <div className="flex gap-4 pb-4">
                      {suggestion.books.map((book) => (
                        <div key={book.id} className="flex flex-col items-center min-w-[120px]">
                          <div className="relative mb-2">
                            <img
                              src={book.coverUrl}
                              alt={book.title}
                              className="w-[120px] h-[180px] object-cover rounded-md shadow-md"
                            />
                            <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-lg">
                              {book.position}
                            </div>
                          </div>
                          <p className="text-xs font-medium text-center line-clamp-2 w-[120px]">
                            {book.title}
                          </p>
                        </div>
                      ))}
                      {suggestion.gaps.map((gapPosition) => {
                        // Check if we have temp data for this position
                        const tempBook = suggestion.tempBooks?.find(t => t.position === gapPosition);
                        
                        return (
                          <div key={`gap-${gapPosition}`} className="flex flex-col items-center min-w-[120px]">
                            <div className="relative mb-2">
                              <div className="w-[120px] h-[180px] bg-muted border-2 border-dashed border-muted-foreground/30 rounded-md flex items-center justify-center">
                                <AlertCircle className="h-8 w-8 text-muted-foreground/50" />
                              </div>
                              <div className="absolute -top-2 -right-2 bg-orange-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-lg">
                                {gapPosition}
                              </div>
                            </div>
                            <p className="text-xs font-medium text-center text-orange-600">
                              {tempBook?.title || 'Missing Book'}
                            </p>
                            {tempBook?.isbn && (
                              <p className="text-xs text-muted-foreground mt-1">ISBN: {tempBook.isbn}</p>
                            )}
                            <Button 
                              size="sm" 
                              variant="link" 
                              className="text-xs mt-1 h-auto p-0"
                              onClick={() => {
                                handleAddBook(
                                  suggestion.id, 
                                  suggestion.seriesName, 
                                  suggestion.authorName, 
                                  gapPosition,
                                  tempBook?.title,
                                  tempBook?.isbn
                                );
                              }}
                            >
                              <Plus className="mr-1 h-3 w-3" />
                              Find & Add
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="verified">
          <div className="mb-4">
            <Button onClick={() => handleAddBook("", "", "")}>
              <Plus className="mr-2 h-4 w-4" />
              Add Book to Series
            </Button>
          </div>
          <div className="space-y-6">
            {/* Approved Suggestions */}
            {allSuggestions?.filter(s => s.status === 'approved').length > 0 && (
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Verified Series Suggestions</h3>
                <div className="space-y-4">
                  {allSuggestions
                    .filter(s => s.status === 'approved')
                    .map((suggestion) => (
                    <Card key={suggestion.id} className="overflow-hidden">
                      <CardHeader className="bg-muted/30">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <CardTitle className="text-xl">{suggestion.seriesName}</CardTitle>
                              <Badge variant="default">AI Verified</Badge>
                            </div>
                            <CardDescription className="text-base">
                              by {suggestion.authorName} • {suggestion.totalBooks || 0} books
                            </CardDescription>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleEditSeries(suggestion)}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Imported Series */}
            {importedSeries && importedSeries.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Series from Imported Data</h3>
                <div className="space-y-4">
                  {importedSeries.map((series, idx) => (
                    <Card key={idx} className="overflow-hidden">
                      <CardHeader className="bg-muted/30">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <CardTitle className="text-xl">{series.seriesName}</CardTitle>
                              <Badge variant="secondary">Imported</Badge>
                            </div>
                            <CardDescription className="text-base">
                              by {series.authorName} • {series.books.length} books
                              {series.expectedBooks > 0 && ` (${series.expectedBooks} expected)`}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {(!allSuggestions?.filter(s => s.status === 'approved').length && !importedSeries?.length) && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-muted-foreground text-center py-8">No verified series yet</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="gaps">
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground text-center py-8">Series with missing books will be displayed here</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all">
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground text-center py-8">All series will be displayed here</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AISuggestSeriesDialog
        open={aiSuggestOpen}
        onOpenChange={setAiSuggestOpen}
        onSeriesSelected={handleAISeriesSelected}
      />

      <SearchSeriesBookDialog
        open={searchBookOpen}
        onOpenChange={setSearchBookOpen}
        seriesName={selectedSeries?.seriesName}
        authorName={selectedSeries?.authorName}
        position={selectedSeries?.position}
        suggestedTitle={selectedSeries?.tempTitle}
        suggestedIsbn={selectedSeries?.tempIsbn}
        onBookSelected={handleBookSelected}
      />

      <EditSeriesDialog
        open={editSeriesDialogOpen}
        onOpenChange={setEditSeriesDialogOpen}
        series={editingSeries}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['series-suggestions'] });
        }}
      />
      </div>
    </div>
  );
}
